/**
 * batch-69 planbord-afspraak (FO-523, spec 223) - afspraak plannen met een klant.
 *
 * Een afspraak is een gesprek met een contactpersoon van een klant, gepland vanaf
 * het planbord (klant slepen op medewerker x dag), de relatie of de
 * acquisitie-lead. Het is GEEN werkbon: er gaan geen uren naar verloning of
 * facturatie. Elke afspraak heeft precies één CRM-contactmoment (zelfde
 * transactie); na afloop maakt een deelnemer een kort verplicht gespreksverslag.
 *
 * Dit bestand is de ENE bron voor de request-schema's: de API valideert ermee
 * (`safeParse` + `sendValidationError`) en de formulieren (backoffice-dialoog,
 * portal-verslag) valideren er client-side mee voor ze iets versturen. Zo kan een
 * formulier niets sturen dat de API weigert.
 */

import { z } from 'zod'

// ─── Enums (spiegel van schema.prisma, sectie batch-69 planbord-afspraak) ─────

export const PLANNING_AFSPRAAK_STATUSSEN = ['GEPLAND', 'AFGEROND', 'AFGEZEGD'] as const
export type PlanningAfspraakStatus = (typeof PLANNING_AFSPRAAK_STATUSSEN)[number]

export const PLANNING_AFSPRAAK_STATUS_LABELS: Record<PlanningAfspraakStatus, string> = {
  GEPLAND: 'Gepland',
  AFGEROND: 'Afgerond',
  AFGEZEGD: 'Afgezegd',
}

export const PLANNING_AFSPRAAK_LOCATIE_SOORTEN = ['KLANTLOCATIE', 'TEAMS', 'TELEFOON', 'ANDERS'] as const
export type PlanningAfspraakLocatieSoort = (typeof PLANNING_AFSPRAAK_LOCATIE_SOORTEN)[number]

export const PLANNING_AFSPRAAK_LOCATIE_LABELS: Record<PlanningAfspraakLocatieSoort, string> = {
  KLANTLOCATIE: 'Bij de klant op locatie',
  TEAMS: 'Teams',
  TELEFOON: 'Telefonisch',
  ANDERS: 'Andere plek',
}

export const PLANNING_AFSPRAAK_REDENEN = [
  'KENNISMAKING',
  'EVALUATIE',
  'OFFERTE',
  'KLACHT',
  'PLANNING_CAPACITEIT',
  'CONTRACTVERLENGING',
  'STORING_NABESPREKING',
  'OVERIG',
] as const
export type PlanningAfspraakReden = (typeof PLANNING_AFSPRAAK_REDENEN)[number]

export const PLANNING_AFSPRAAK_REDEN_LABELS: Record<PlanningAfspraakReden, string> = {
  KENNISMAKING: 'Kennismaking',
  EVALUATIE: 'Evaluatie',
  OFFERTE: 'Offerte of aanbieding',
  KLACHT: 'Klachtbespreking',
  PLANNING_CAPACITEIT: 'Planning en capaciteit',
  CONTRACTVERLENGING: 'Contractverlenging',
  STORING_NABESPREKING: 'Storingsnabespreking',
  OVERIG: 'Overig',
}

export const PLANNING_AFSPRAAK_INGANGEN = ['PLANBORD', 'RELATIE', 'ACQUISITIE', 'VERSLAG_VERVOLG'] as const
export type PlanningAfspraakIngang = (typeof PLANNING_AFSPRAAK_INGANGEN)[number]

export const AFSPRAAK_VERSLAG_UITKOMSTEN = [
  'AFSPRAKEN_GEMAAKT',
  'VERVOLG_NODIG',
  'OFFERTE_OPSTELLEN',
  'GEEN_ACTIE',
  'ESCALATIE',
] as const
export type AfspraakVerslagUitkomst = (typeof AFSPRAAK_VERSLAG_UITKOMSTEN)[number]

export const AFSPRAAK_VERSLAG_UITKOMST_LABELS: Record<AfspraakVerslagUitkomst, string> = {
  AFSPRAKEN_GEMAAKT: 'Afspraken gemaakt',
  VERVOLG_NODIG: 'Vervolg nodig',
  OFFERTE_OPSTELLEN: 'Offerte opstellen',
  GEEN_ACTIE: 'Geen actie',
  ESCALATIE: 'Escalatie',
}

export const CONTACT_MOMENT_ZICHTBAARHEDEN = ['OPENBAAR', 'AFGESCHERMD'] as const
export type ContactMomentZichtbaarheid = (typeof CONTACT_MOMENT_ZICHTBAARHEDEN)[number]

export const CONTACT_MOMENT_ZICHTBAARHEID_LABELS: Record<ContactMomentZichtbaarheid, string> = {
  OPENBAAR: 'Openbaar',
  AFGESCHERMD: 'Afgeschermd',
}

/** Uitleg bij de keuze (formulieren in backoffice en portal: één tekst). */
export const CONTACT_MOMENT_ZICHTBAARHEID_UITLEG: Record<ContactMomentZichtbaarheid, string> = {
  OPENBAAR: 'Iedereen met leesrecht op het CRM ziet de inhoud.',
  AFGESCHERMD:
    'Alleen de auteur, de deelnemers, de accountmanager van de klant en de doelgroep CRM-vertrouwelijk zien de inhoud. Anderen zien alleen dat er een afspraak was.',
}

/** Wat een viewer zonder recht te zien krijgt in plaats van het onderwerp. */
export const AFGESCHERMD_ONDERWERP = 'Afgeschermde afspraak'
export const AFGESCHERMD_CONTACTMOMENT_ONDERWERP = 'Afgeschermd contactmoment'
export const AFGESCHERMD_VERSLAG_LABEL = 'Afgeschermd verslag'

/** De keuzes voor de herinnering vooraf (minuten; null = geen). Default 1 uur. */
export const AFSPRAAK_HERINNERING_OPTIES: ReadonlyArray<{ minuten: number | null; label: string }> = [
  { minuten: null, label: 'Geen herinnering' },
  { minuten: 15, label: '15 minuten vooraf' },
  { minuten: 30, label: '30 minuten vooraf' },
  { minuten: 60, label: '1 uur vooraf' },
  { minuten: 120, label: '2 uur vooraf' },
  { minuten: 1440, label: '1 dag vooraf' },
]
export const AFSPRAAK_HERINNERING_DEFAULT_MINUTEN = 60
/** Hooguit twee dagen vooraf: zo ver kijkt de cron `planning-afspraak-verslag` vooruit. */
export const AFSPRAAK_HERINNERING_MAX_MINUTEN = 2880

// ─── Grenzen ─────────────────────────────────────────────────────────────────

export const AFSPRAAK_VERSLAG_SAMENVATTING_MIN = 10
export const AFSPRAAK_VERSLAG_SAMENVATTING_MAX = 500
export const AFSPRAAK_VOORBEREIDING_MIN = 3
export const AFSPRAAK_VOORBEREIDING_MAX = 2000
export const AFSPRAAK_REDEN_TOELICHTING_MAX = 200
export const AFSPRAAK_MAX_DEELNEMERS = 10
export const AFSPRAAK_MAX_ACTIEPUNTEN = 20
export const AFSPRAAK_AFZEG_REDEN_MIN = 3

// ─── Gedeelde veld-schema's ──────────────────────────────────────────────────

const datumVeld = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Kies een geldige datum')
/** "HH:mm" op het kwartier (zelfde regel als de werkbon-tijden). */
const kwartierTijdVeld = z
  .string({ error: 'Kies een tijd' })
  .regex(/^([01]\d|2[0-3]):(00|15|30|45)$/, 'Kies een tijd op het kwartier (bijv. 10:15)')
const idVeld = (melding: string) => z.string({ error: melding }).trim().min(1, melding)

// ─── Afspraak plannen / wijzigen ─────────────────────────────────────────────

const afspraakVelden = z.object({
  contactId: idVeld('Kies een contactpersoon'),
  datum: datumVeld,
  startTijd: kwartierTijdVeld,
  eindTijd: kwartierTijdVeld,
  locatieSoort: z.enum(PLANNING_AFSPRAAK_LOCATIE_SOORTEN, { message: 'Kies waar de afspraak is' }),
  clientLocationId: z.string().trim().min(1).nullable().optional(),
  locatieOmschrijving: z.string().trim().max(200).nullable().optional(),
  reden: z.enum(PLANNING_AFSPRAAK_REDENEN, { message: 'Kies een reden' }),
  redenToelichting: z.string().trim().max(AFSPRAAK_REDEN_TOELICHTING_MAX).nullable().optional(),
  /** De input/voorbereiding: wat wil je bereiken, wat neem je mee. Wordt de notitie van het contactmoment. */
  voorbereiding: z
    .string({ error: 'Schrijf kort wat je wilt bereiken' })
    .trim()
    .min(AFSPRAAK_VOORBEREIDING_MIN, 'Schrijf kort wat je wilt bereiken en wat je meeneemt')
    .max(AFSPRAAK_VOORBEREIDING_MAX),
  deelnemerEmployeeIds: z
    .array(z.string().trim().min(1))
    .min(1, 'Kies minimaal een deelnemer')
    .max(AFSPRAAK_MAX_DEELNEMERS, `Maximaal ${AFSPRAAK_MAX_DEELNEMERS} deelnemers`),
  // Geen defaults hier: bij WIJZIGEN betekent "niet meegestuurd" ongewijzigd laten
  // (een weggelaten zichtbaarheid mag een afgeschermde afspraak nooit openbaar maken).
  // De defaults voor een nieuwe afspraak staan op `afspraakPlanSchema`.
  herinneringMinutenVooraf: z
    .number()
    .int()
    .min(0)
    .max(AFSPRAAK_HERINNERING_MAX_MINUTEN, 'Een herinnering kan hooguit twee dagen vooraf')
    .nullable()
    .optional(),
  zichtbaarheid: z.enum(CONTACT_MOMENT_ZICHTBAARHEDEN).optional(),
  /** Spec 215 (km-balans): open veld, deze stream rekent er niets mee. */
  reisKmMeetellen: z.boolean().optional(),
  /** Conflict (dienst, werkbon, verlof, andere afspraak) bewust geaccepteerd. */
  force: z.boolean().optional(),
})

type AfspraakVeldenInvoer = z.infer<typeof afspraakVelden>

function controleerAfspraak(v: AfspraakVeldenInvoer, ctx: z.RefinementCtx): void {
  if (v.eindTijd <= v.startTijd) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['eindTijd'], message: 'De eindtijd moet na de begintijd liggen' })
  }
  if (v.locatieSoort === 'KLANTLOCATIE' && !v.clientLocationId) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['clientLocationId'], message: 'Kies de locatie van de klant' })
  }
  if (v.locatieSoort === 'ANDERS' && !v.locatieOmschrijving?.trim()) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['locatieOmschrijving'], message: 'Vul in waar de afspraak is' })
  }
  if (v.reden === 'OVERIG' && !v.redenToelichting?.trim()) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['redenToelichting'], message: 'Licht de reden kort toe' })
  }
  if (new Set(v.deelnemerEmployeeIds).size !== v.deelnemerEmployeeIds.length) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['deelnemerEmployeeIds'], message: 'Een deelnemer staat er dubbel in' })
  }
}

/** POST /planning-afspraken - een afspraak plannen (planbord, relatie, lead). */
export const afspraakPlanSchema = afspraakVelden
  .extend({
    herinneringMinutenVooraf: z
      .number()
      .int()
      .min(0)
      .max(AFSPRAAK_HERINNERING_MAX_MINUTEN, 'Een herinnering kan hooguit twee dagen vooraf')
      .nullable()
      .default(AFSPRAAK_HERINNERING_DEFAULT_MINUTEN),
    zichtbaarheid: z.enum(CONTACT_MOMENT_ZICHTBAARHEDEN).default('OPENBAAR'),
    reisKmMeetellen: z.boolean().default(false),
    clientId: idVeld('Kies een klant'),
    ingang: z.enum(['PLANBORD', 'RELATIE', 'ACQUISITIE']).default('PLANBORD'),
    /** Draad (FO-399): deze afspraak is een vervolg op een eerder contactmoment. */
    vervolgOpId: z.string().trim().min(1).nullable().optional(),
  })
  .superRefine(controleerAfspraak)
export type AfspraakPlanInput = z.input<typeof afspraakPlanSchema>
export type AfspraakPlanData = z.output<typeof afspraakPlanSchema>

/**
 * PATCH /planning-afspraken/:id - de hele afspraak opnieuw (klant blijft vast).
 * Herinnering, zichtbaarheid en reiskilometers: weggelaten = ongewijzigd.
 */
export const afspraakWijzigSchema = afspraakVelden.superRefine(controleerAfspraak)
export type AfspraakWijzigInput = z.input<typeof afspraakWijzigSchema>
export type AfspraakWijzigData = z.output<typeof afspraakWijzigSchema>

/**
 * POST /planning-afspraken/:id/verplaats - slepen op het planbord, of een ander
 * tijdvak kiezen zonder de inhoud te zien (een planner zonder CRM-recht kan zo
 * een openbare afspraak van een collega naar 14:00 schuiven). Begin- en eindtijd
 * samen of geen van beide; weggelaten = de huidige tijden blijven.
 */
export const afspraakVerplaatsSchema = z
  .object({
    datum: datumVeld,
    startTijd: kwartierTijdVeld.optional(),
    eindTijd: kwartierTijdVeld.optional(),
    /** Van welke deelnemer-rij werd gesleept (alleen nodig bij een andere medewerker). */
    vanEmployeeId: z.string().trim().min(1).nullable().optional(),
    naarEmployeeId: z.string().trim().min(1).nullable().optional(),
    force: z.boolean().optional(),
  })
  .superRefine((v, ctx) => {
    if ((v.startTijd === undefined) !== (v.eindTijd === undefined)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: [v.startTijd === undefined ? 'startTijd' : 'eindTijd'],
        message: 'Kies zowel de begin- als de eindtijd',
      })
      return
    }
    if (v.startTijd !== undefined && v.eindTijd !== undefined && v.eindTijd <= v.startTijd) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['eindTijd'], message: 'De eindtijd moet na de begintijd liggen' })
    }
  })
export type AfspraakVerplaatsInput = z.input<typeof afspraakVerplaatsSchema>

/** POST /planning-afspraken/:id/afzeggen - geen verslagplicht, wel een korte notitie. */
export const afspraakAfzegSchema = z.object({
  doorKlant: z.boolean().default(true),
  reden: z
    .string({ error: 'Schrijf kort waarom de afspraak niet doorgaat' })
    .trim()
    .min(AFSPRAAK_AFZEG_REDEN_MIN, 'Schrijf kort waarom de afspraak niet doorgaat')
    .max(500),
})
export type AfspraakAfzegInput = z.input<typeof afspraakAfzegSchema>

// ─── Het verslag ─────────────────────────────────────────────────────────────

/** Een concrete afspraak uit het gesprek (eigenaar + datum = opvolging). */
export const afspraakVerslagActiepuntSchema = z.object({
  omschrijving: z
    .string({ error: 'Omschrijf de afspraak' })
    .trim()
    .min(3, 'Omschrijf de afspraak')
    .max(300),
  eigenaarEmployeeId: z.string().trim().min(1).nullable().optional(),
  datum: datumVeld.nullable().optional(),
})
export type AfspraakVerslagActiepunt = z.output<typeof afspraakVerslagActiepuntSchema>

/** De vervolgafspraak die een deelnemer vanuit het verslag plant (draad + opvolging). */
export const afspraakVervolgSchema = z
  .object({
    datum: datumVeld,
    startTijd: kwartierTijdVeld,
    eindTijd: kwartierTijdVeld,
    /** Conflict (dienst, werkbon, verlof, andere afspraak) bewust geaccepteerd. */
    force: z.boolean().optional(),
  })
  .superRefine((v, ctx) => {
    if (v.eindTijd <= v.startTijd) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['eindTijd'], message: 'De eindtijd moet na de begintijd liggen' })
    }
  })

/** POST /planning-afspraken/:id/verslag - het korte verplichte gespreksverslag. */
export const afspraakVerslagSchema = z
  .object({
    samenvatting: z
      .string({ error: 'Schrijf een korte samenvatting' })
      .trim()
      .min(AFSPRAAK_VERSLAG_SAMENVATTING_MIN, `Schrijf een korte samenvatting (minimaal ${AFSPRAAK_VERSLAG_SAMENVATTING_MIN} tekens)`)
      .max(AFSPRAAK_VERSLAG_SAMENVATTING_MAX, `Maximaal ${AFSPRAAK_VERSLAG_SAMENVATTING_MAX} tekens`),
    uitkomst: z.enum(AFSPRAAK_VERSLAG_UITKOMSTEN, { message: 'Kies de uitkomst' }),
    afspraken: z.array(afspraakVerslagActiepuntSchema).max(AFSPRAAK_MAX_ACTIEPUNTEN).default([]),
    tevredenheid: z.number().int().min(1).max(5).nullable().optional(),
    /** Weggelaten = de huidige zichtbaarheid van de afspraak blijft (nooit stil openbaar). */
    zichtbaarheid: z.enum(CONTACT_MOMENT_ZICHTBAARHEDEN).optional(),
    vervolg: afspraakVervolgSchema.nullable().optional(),
  })
  .superRefine((v, ctx) => {
    if (v.uitkomst === 'VERVOLG_NODIG' && !v.vervolg && !v.afspraken.some((a) => a.datum)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['vervolg'],
        message: 'Plan de vervolgafspraak, of zet een datum bij een van de afspraken',
      })
    }
  })
export type AfspraakVerslagInput = z.input<typeof afspraakVerslagSchema>
export type AfspraakVerslagData = z.output<typeof afspraakVerslagSchema>

/** POST /planning-afspraken/:id/verslag/aanvulling - een deelnemer vult het verslag aan. */
export const afspraakVerslagAanvullingSchema = z.object({
  tekst: z
    .string({ error: 'Schrijf je aanvulling' })
    .trim()
    .min(3, 'Schrijf je aanvulling')
    .max(AFSPRAAK_VERSLAG_SAMENVATTING_MAX),
})

/** PATCH /contact-momenten/:id/zichtbaarheid - alleen auteur of CRM-vertrouwelijk, met audit. */
export const contactMomentZichtbaarheidSchema = z.object({
  zichtbaarheid: z.enum(CONTACT_MOMENT_ZICHTBAARHEDEN),
  toelichting: z.string().trim().max(300).nullable().optional(),
})

/** POST /contact-momenten/:id/auteur - overdracht van auteurschap (CRM-vertrouwelijk). */
export const contactMomentAuteurSchema = z.object({
  naarUserId: idVeld('Kies de nieuwe auteur'),
  toelichting: z.string().trim().max(300).nullable().optional(),
})

// ─── Instellingen (doelgroep CRM-vertrouwelijk + termijnen) ──────────────────

/** Rollen die in de doelgroep CRM-vertrouwelijk kunnen. Nooit ACCOUNTANT (FO-404). */
export const CRM_VERTROUWELIJK_ROLLEN = ['TEAMLEIDER', 'PLANNER', 'HR', 'SALARIS', 'MANAGER', 'ADMIN'] as const
export type CrmVertrouwelijkRol = (typeof CRM_VERTROUWELIJK_ROLLEN)[number]

export const CRM_AFSPRAAK_INSTELLINGEN_KEY = 'CRM_AFSPRAAK_INSTELLINGEN'

/** Een heel getal binnen een bereik, met Nederlandse meldingen (inline in het formulier). */
const geheelGetal = (min: number, max: number) =>
  z
    .number({ error: 'Vul een getal in' })
    .int('Vul een heel getal in')
    .min(min, `Minimaal ${min}`)
    .max(max, `Maximaal ${max}`)

export const crmAfspraakInstellingenSchema = z.object({
  /** Verslag vragen zoveel minuten na de eindtijd (spec: circa een kwartier). */
  verslagVraagNaMinuten: geheelGetal(0, 240).default(15),
  /** Eerste herinnering zoveel uur na de eindtijd. */
  herinneringNaUren: geheelGetal(1, 24).default(4),
  /** Tweede herinnering: de ochtend na de afspraak om dit uur (NL-tijd). */
  ochtendHerinneringUur: geheelGetal(5, 12).default(8),
  /** Signaal aan teamleider en accountmanager zoveel uur na de eindtijd. */
  escalatieNaUren: geheelGetal(12, 168).default(48),
  /** Doelgroep CRM-vertrouwelijk: deze rollen ... */
  vertrouwelijkRollen: z.array(z.enum(CRM_VERTROUWELIJK_ROLLEN)).default(['HR']),
  /** ... plus de directie (medewerkers met de directie-vlag) ... */
  vertrouwelijkDirectie: z.boolean().default(true),
  /** ... plus deze personen (User.id). */
  vertrouwelijkUserIds: z.array(z.string().trim().min(1)).max(50).default([]),
})
export type CrmAfspraakInstellingen = z.output<typeof crmAfspraakInstellingenSchema>
export type CrmAfspraakInstellingenInput = z.input<typeof crmAfspraakInstellingenSchema>

export const CRM_AFSPRAAK_INSTELLINGEN_DEFAULTS: CrmAfspraakInstellingen = crmAfspraakInstellingenSchema.parse({})

// ─── DTO's ───────────────────────────────────────────────────────────────────

/** De verslagstatus van een afspraak, server-side afgeleid (één regel, `afspraakVerslagStatus`). */
export type AfspraakVerslagStatus = 'NIET_NODIG' | 'NOG_NIET' | 'TE_MAKEN' | 'GEMAAKT'

export const AFSPRAAK_VERSLAG_STATUS_LABELS: Record<AfspraakVerslagStatus, string> = {
  NIET_NODIG: 'Geen verslag nodig',
  NOG_NIET: 'Verslag na afloop',
  TE_MAKEN: 'Verslag ontbreekt',
  GEMAAKT: 'Verslag gemaakt',
}

export interface AfspraakDeelnemerDTO {
  employeeId: string
  naam: string
}

export interface AfspraakVerslagDTO {
  samenvatting: string
  uitkomst: AfspraakVerslagUitkomst | null
  afspraken: Array<AfspraakVerslagActiepunt & { eigenaarNaam: string | null }>
  auteurNaam: string | null
  op: string | null
}

export interface ContactMomentBijlageDTO {
  id: string
  bestandsnaam: string
  mimeType: string
  grootte: number
}

export interface PlanningAfspraakRechtenDTO {
  /** De hele afspraak wijzigen: plannen-recht EN de inhoud mogen zien. */
  bewerken: boolean
  /** Dag en tijd wijzigen of verplaatsen (plannen-recht, ook zonder de inhoud te zien). */
  tijdvakWijzigen: boolean
  afzeggen: boolean
  /** Een deelnemer (of de auteur) die het verslag mag maken of aanvullen. */
  verslag: boolean
  /** Auteur of doelgroep CRM-vertrouwelijk. */
  zichtbaarheid: boolean
  /** Alleen de doelgroep CRM-vertrouwelijk. */
  auteurOverdragen: boolean
}

export interface PlanningAfspraakDTO {
  id: string
  contactMomentId: string
  clientId: string
  clientNaam: string
  contact: { id: string; naam: string; functie: string | null; telefoon: string | null; email: string | null } | null
  /** yyyy-mm-dd (NL-kalenderdag). */
  datum: string
  startTijd: string
  eindTijd: string
  locatieSoort: PlanningAfspraakLocatieSoort
  locatie: { id: string; naam: string; adres: string | null } | null
  locatieOmschrijving: string | null
  /** null bij een afgeschermde afspraak voor deze viewer. */
  reden: PlanningAfspraakReden | null
  redenToelichting: string | null
  /** De input/voorbereiding (notitie van het contactmoment); null = afgeschermd. */
  voorbereiding: string | null
  deelnemers: AfspraakDeelnemerDTO[]
  herinneringMinutenVooraf: number | null
  status: PlanningAfspraakStatus
  afzegReden: string | null
  zichtbaarheid: ContactMomentZichtbaarheid
  /**
   * True = de inhoud (reden, input, verslag, score, bijlagen) is voor deze viewer
   * server-side weggelaten. Twee oorzaken, te onderscheiden via `zichtbaarheid`:
   * de afspraak is afgeschermd, of de viewer heeft geen CRM-leesrecht en is geen
   * betrokkene. Let op: breder dan `PlanningBoardAfspraakDTO.afgeschermd`.
   */
  afgeschermd: boolean
  verslagStatus: AfspraakVerslagStatus
  verslag: AfspraakVerslagDTO | null
  tevredenheid: number | null
  bijlagen: ContactMomentBijlageDTO[]
  auteurNaam: string | null
  vervolgOpId: string | null
  reisKmMeetellen: boolean
  ingang: PlanningAfspraakIngang
  rechten: PlanningAfspraakRechtenDTO
}

/** Het afspraak-blok op het planbord (per deelnemer-rij). */
export interface PlanningBoardAfspraakDTO {
  id: string
  /** yyyy-mm-dd (NL-kalenderdag). */
  datum: string
  startTijd: string
  eindTijd: string
  status: PlanningAfspraakStatus
  clientId: string
  clientNaam: string
  contactNaam: string | null
  locatieLabel: string
  /** null als de viewer de inhoud niet mag zien (afgeschermd, of geen CRM-leesrecht). */
  redenLabel: string | null
  /**
   * True = ECHT afgeschermd en voor deze viewer verborgen (het slot-icoon). Zonder
   * CRM-leesrecht ontbreekt de reden ook, maar dan blijft dit false: het blok is
   * dan niet afgeschermd, de inhoud hoort alleen niet bij die rol. Smaller dan
   * `PlanningAfspraakDTO.afgeschermd` (detail).
   */
  afgeschermd: boolean
  /**
   * Mag deze kijker het blok naar een andere medewerker slepen? Alleen met
   * inhoud-zicht (een nieuwe deelnemer leest de inhoud); anders alleen een andere
   * dag in dezelfde rij. De API toetst het ook.
   */
  deelnemerWisselen: boolean
  verslagStatus: AfspraakVerslagStatus
  /** De andere deelnemers (namen), voor de tooltip "samen met ...". */
  medeDeelnemers: string[]
}

/** Opties voor de plan-dialoog (contactpersonen, locaties, medewerkers) van één klant. */
export interface AfspraakPlanOptiesDTO {
  client: { id: string; naam: string }
  contactpersonen: Array<{ id: string; naam: string; functie: string | null }>
  locaties: Array<{ id: string; naam: string; adres: string | null; isPrimair: boolean }>
  medewerkers: Array<{ id: string; naam: string; functie: string | null }>
  /** De accountmanager (eindverantwoordelijke) van de klant, als hij er is. */
  accountmanagerEmployeeId: string | null
}

/** POST /planning-afspraken/opties/contactpersoon - inline een nieuwe contactpersoon. */
export const afspraakNieuweContactpersoonSchema = z.object({
  clientId: idVeld('Kies een klant'),
  firstName: z.string({ error: 'Vul de voornaam in' }).trim().min(1, 'Vul de voornaam in').max(100),
  lastName: z.string({ error: 'Vul de achternaam in' }).trim().min(1, 'Vul de achternaam in').max(100),
  functionTitle: z.string().trim().max(100).nullable().optional(),
  phone: z.string().trim().max(40).nullable().optional(),
  email: z.string().trim().email('Ongeldig e-mailadres').max(200).nullable().optional().or(z.literal('')),
})
export type AfspraakNieuweContactpersoonInput = z.input<typeof afspraakNieuweContactpersoonSchema>

/** Activiteit per contactpersoon op de relatie (tellers server-side over de volledige set). */
export interface RelatieAfsprakenActiviteitDTO {
  tellers: {
    afsprakenDitJaar: number
    openOpvolgingen: number
    laatsteTevredenheid: { score: number; op: string } | null
  }
  afspraken: PlanningAfspraakDTO[]
  /** Contactpersonen met minstens een contactmoment, voor het filter. */
  contactpersonen: Array<{ id: string; naam: string; aantal: number }>
  totaal: number
  afgekapt: boolean
}

/** CRM-rapportage: afspraken per klant, medewerker en reden + verslag-doorlooptijd. */
export interface AfsprakenRapportageRijDTO {
  sleutel: string
  label: string
  gepland: number
  afgerond: number
  afgezegd: number
  zonderVerslag: number
  /** Gemiddelde uren tussen eindtijd en verslag (alleen afgerond met verslag); null = geen. */
  gemiddeldeDoorlooptijdUren: number | null
}

export interface AfsprakenRapportageDTO {
  van: string
  totEnMet: string
  totaal: number
  perKlant: AfsprakenRapportageRijDTO[]
  perMedewerker: AfsprakenRapportageRijDTO[]
  perReden: AfsprakenRapportageRijDTO[]
}
